import { Frame, useEditor } from "@craftjs/core";
import { GrigoraEditorContext } from "../context";
import React, {
  useEffect,
  useContext,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { emptyPage, isJsonString } from "../../helpers";
import { createPortal } from "react-dom";
import Loader from "../../loader/loader";
import { GrigoraDashboardContext } from "../../dashboard/context";
import { Googlefontloader, Tooltip } from "../../input";
import {
  createComponentPage,
  updatePageWithComponents,
} from "../component-blocks";
import { rawCopyHandler } from "../../raw-parser";
import { useRefCallback, useScript } from "../../hooks";
import { optimizeNodeTree } from "../optimize-node-tree";
import Toast from "../../toast/toast";
import { CorrectIcon } from "../../../assets/ui-icons";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong, faRefresh } from "@fortawesome/free-solid-svg-icons";
import ContextMenu from "./contextMenu";
import { set, throttle } from "lodash";
import { isMac, typographyCSSRaw } from "../../utils";
import blockActions from "../../block-actions";
import { getTempStateLocalStorage } from "../../local-store";
import { isTextSelected } from "../../dom";
import GoogleFonts from "./../../input/gfonts.json";
import { GrigoraFormatsContext } from "../../formats";
import CustomFontLoader from "../../input/customfontloader";

const SYSTEM_FONT =
  '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen-Sans,Ubuntu,Cantarell,"Helvetica Neue",sans-serif';

export const FunctionalIFrameComponent = ({
  isComponentPage,
  children,
  title,
  ...props
}) => {
  const {
    editorDocument = document,
    setEditorDocument,
    editorWindow = window,
    setEditorWindow,
  } = useContext(GrigoraDashboardContext);

  const { displaySize, currentPage, prevPage, setPrevPage, changeCurrentPage } =
    useContext(GrigoraEditorContext);

  const [contentRef, setContentRef] = useState(null);

  const mountNode = contentRef?.contentWindow?.document?.body;

  useEffect(() => {
    setEditorDocument(window.frames["iframe-renderer"].contentDocument);
    setEditorWindow(window.frames["iframe-renderer"].contentWindow);
  }, [mountNode]);

  return (
    <>
      <div
        className={`w-[100%] h-full m-auto mr-auto ${
          isComponentPage && displaySize !== "desktop"
            ? "mt-3"
            : isComponentPage
            ? "mt-8"
            : ""
        } ${
          isComponentPage && displaySize === "desktop" ? "scale-75" : ""
        } duration-300`}
        style={{
          maxWidth:
            displaySize === "tablet"
              ? "800px"
              : displaySize === "mobile"
              ? "360px"
              : isComponentPage
              ? "100%"
              : "100%",

          maxHeight:
            displaySize === "tablet"
              ? "90%"
              : displaySize === "mobile"
              ? "80%"
              : isComponentPage
              ? "70%"
              : "100%",
        }}
      >
        {currentPage.toString().startsWith("component-") && (
          <Tooltip text={`Go Back`} mouseEnterDelay={1} placement="bottom">
            <div className="flex gap-4 items-center mb-4">
              <div
                onClick={() => {
                  setPrevPage(prevPage);
                  changeCurrentPage(prevPage);
                }}
                className="flex justify-center items-center w-8 h-8 bg-white rounded-full  cursor-pointer hover:bg-gray-100 transition-all duration-300"
              >
                <FontAwesomeIcon icon={faArrowLeftLong} />
              </div>
              <div>Go Back</div>
            </div>
          </Tooltip>
        )}
        <iframe
          title={title}
          {...props}
          ref={setContentRef}
          className="w-[100%] ml-auto mr-auto h-full bg-white duration-300 shadow-editor-elements"
          id="iframe-renderer"
        >
          {mountNode && createPortal(children, mountNode)}
        </iframe>
      </div>
    </>
  );
};

function ElementCanvas(props) {
  const {
    selected,
    connectors,
    query,
    actions,
    store,
    actions: { deserialize },
  } = useEditor((state) => ({
    enabled: state.options.enabled,
    selected: state.events.selected,
  }));

  // const forceUpdate = useForceUpdate();
  const { activeFormats, applyFormat, removeFormat } =
    useContext(GrigoraFormatsContext) || {};
  const { project, editorDocument, setEditorDocument, setEditorWindow } =
    useContext(GrigoraDashboardContext);
  const {
    currentPage,
    pageContents,
    isNewPageContentLoading,
    project: projectEditorData,
    projectComponents,
    failedLoadingPageContents,
    changeCurrentPage,
    handleContextMenu,
    isAddingBlocks,
    setIsAddingBlocks,
    isAddingSections,
    setIsAddingSections,
    leftWidget,
    setLeftWidget,
    hideContextMenu,
    isHomeMenuOpen,
    setIsHomeMenuOpen,
    isPublishMenuOpen,
    setIsPublishMenuOpen,
    hasShownBlockSettingFirstTime,
    setHasShownBlockSettingFirstTime,
    isBlockSettingsVisible,
    setIsBlockSettingsVisible,
  } = useContext(GrigoraEditorContext);

  const canvasRef = useRef();

  const helperRef = useRef();

  useEffect(() => {
    if (failedLoadingPageContents[currentPage]) {
      return;
    }

    if (currentPage && currentPage.startsWith("component-")) {
      if (
        projectComponents[currentPage] &&
        projectComponents[currentPage].root_node
      ) {
        var tmpPage = createComponentPage(projectComponents[currentPage]);

        tmpPage = optimizeNodeTree(tmpPage);
        // tmpPage = updatePageWithComponents(tmpPage, projectComponents);
        deserialize(isJsonString(tmpPage) ? tmpPage : emptyPage());
        store.history.clear();
      }
    } else {
      var tmpPage = pageContents[currentPage];
      tmpPage = updatePageWithComponents(tmpPage, projectComponents, query);

      tmpPage = optimizeNodeTree(tmpPage);

      deserialize(isJsonString(tmpPage) ? tmpPage : emptyPage());
      store.history.clear();
    }
  }, [currentPage]);

  function getWebfontFamilies() {
    if (
      projectEditorData &&
      projectEditorData.fonts &&
      typeof projectEditorData.fonts === "object"
    ) {
      var fonts = [];
      Object.keys(projectEditorData.fonts).map((font) => {
        let fontvars = [];
        fontvars.push(projectEditorData.fonts[font].family);
        fontvars.push(
          GoogleFonts?.[projectEditorData.fonts[font].family]?.variants?.join(
            ",",
          ),
        );
        fontvars.push(
          GoogleFonts?.[projectEditorData.fonts[font].family]?.subsets?.join(
            ",",
          ),
        );
        fontvars = fontvars.filter((item) => item);
        fonts.push(fontvars.join(":"));
      });
      return fonts;
    }
    return [];
  }

  const webloaderFamilies = useMemo(
    () => getWebfontFamilies(),
    [projectEditorData ? projectEditorData?.fonts : projectEditorData],
  );

  const copyHandler = useRefCallback(
    (event) => {
      rawCopyHandler(event, actions, query, editorDocument);
    },
    [editorDocument],
  );

  const scrollIntoViewIfNeeded = useCallback(
    (target) => {
      if (!target) {
        return;
      }

      if (
        target.getBoundingClientRect()?.bottom >
        editorDocument?.documentElement?.clientHeight
      ) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }

      if (target.getBoundingClientRect()?.top < 0) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    },
    [editorDocument],
  );

  useEffect(() => {
    const selectedNodeId = selected?.values()?.next()?.value;

    const dom = editorDocument?.querySelector(
      `[data-grigora-block-id='${selectedNodeId}']`,
    );

    scrollIntoViewIfNeeded(dom);

    if (selectedNodeId && !hasShownBlockSettingFirstTime) {
      setIsBlockSettingsVisible(true);
      setHasShownBlockSettingFirstTime(true);
    }
  }, [selected]);

  // throttle on drag over
  const throttledOnDragOver = useRef(
    throttle((e) => {
      if (helperRef.current) {
        helperRef.current.style.pointerEvents = "none";
      }
      document.querySelector(".elements-widget").style.display = "none";
    }, 200),
  );

  // Set Pointer Event to none if isaddingblocks, isaddingsections is false
  useEffect(() => {
    if (leftWidget === "pages" || leftWidget === "components") {
      if (helperRef.current) {
        helperRef.current.style.pointerEvents = "auto";
      }
    }

    if (isHomeMenuOpen) {
      if (helperRef.current) {
        helperRef.current.style.pointerEvents = "auto";
      }
    }

    if (isPublishMenuOpen) {
      if (helperRef.current) {
        helperRef.current.style.pointerEvents = "auto";
      }
    }

    if (!isAddingBlocks && !isAddingSections) {
      if (helperRef.current) {
        helperRef.current.style.pointerEvents = "none";
      }
      return;
    }

    if (helperRef.current) {
      helperRef.current.style.pointerEvents = "none";
    }
  }, [
    isAddingBlocks,
    isAddingSections,
    leftWidget,
    isHomeMenuOpen,
    isPublishMenuOpen,
  ]);

  useEffect(() => {
    if (!editorDocument) {
      return;
    }

    const script = editorDocument.createElement("script");

    script.id = "grigora-editor-onload";

    // Onload
    script.src = `${process.env.PUBLIC_URL}/js/editor-onload.js`;
    // script.async = true;

    editorDocument.body.appendChild(script);

    return () => {
      // Run a query selector and remove it
      const script = editorDocument.querySelector("#grigora-editor-onload");
      if (script) {
        script.remove();
      }
    };
  }, [editorDocument]);

  const typographyCSS = useMemo(() => {
    var typography = projectEditorData?.typography || {};

    var css = typographyCSSRaw(typography);

    return css;
  }, [projectEditorData?.typography]);

  const fontFamilyCSS = useMemo(() => {
    return `
    .grigora-editor-renderer-canvas {
      ${
        projectEditorData?.fonts && projectEditorData?.fonts["body"]
          ? `font-family: ${projectEditorData.fonts["body"].family};`
          : `font-family: ${SYSTEM_FONT};`
      }
    }
    .grigora-editor-renderer-canvas h1, .grigora-editor-renderer-canvas h2, .grigora-editor-renderer-canvas h3, .grigora-editor-renderer-canvas h4, .grigora-editor-renderer-canvas h5, .grigora-editor-renderer-canvas h6{
      ${
        projectEditorData?.fonts && projectEditorData?.fonts["h*"]
          ? `font-family: ${projectEditorData.fonts["h*"].family};`
          : ``
      }
    }
    .grigora-editor-renderer-canvas h1{
      ${
        projectEditorData?.fonts && projectEditorData?.fonts["h1"]
          ? `font-family: ${projectEditorData.fonts["h1"].family};`
          : ``
      }
    }
    .grigora-editor-renderer-canvas h2{
      ${
        projectEditorData?.fonts && projectEditorData?.fonts["h2"]
          ? `font-family: ${projectEditorData.fonts["h2"].family};`
          : ``
      }
    }
    .grigora-editor-renderer-canvas h3{
      ${
        projectEditorData?.fonts && projectEditorData?.fonts["h3"]
          ? `font-family: ${projectEditorData.fonts["h3"].family};`
          : ``
      }
    }
    .grigora-editor-renderer-canvas h4{
      ${
        projectEditorData?.fonts && projectEditorData?.fonts["h4"]
          ? `font-family: ${projectEditorData.fonts["h4"].family};`
          : ``
      }
    }
    .grigora-editor-renderer-canvas h5{
      ${
        projectEditorData?.fonts && projectEditorData?.fonts["h5"]
          ? `font-family: ${projectEditorData.fonts["h5"].family};`
          : ``
      }
    }
    .grigora-editor-renderer-canvas h6{
      ${
        projectEditorData?.fonts && projectEditorData?.fonts["h6"]
          ? `font-family: ${projectEditorData.fonts["h6"].family};`
          : ``
      }
    }
    .grigora-editor-renderer-canvas .g-site-title h1 ,
    .grigora-editor-renderer-canvas .g-site-title h2,
    .grigora-editor-renderer-canvas .g-site-title h3,
    .grigora-editor-renderer-canvas .g-site-title h4,
    .grigora-editor-renderer-canvas .g-site-title h5,
    .grigora-editor-renderer-canvas .g-site-title h6,
    .grigora-editor-renderer-canvas .g-site-title p{
      ${
        projectEditorData?.fonts && projectEditorData?.fonts["site-title"]
          ? `font-family: ${projectEditorData.fonts["site-title"].family};`
          : ``
      }
    }`;
  }, [projectEditorData?.fonts]);

  const removeAllFormat = (list) => {
    for (let item of list) {
      removeFormat(item);
    }
  };

  const handleOnKeyDown = useRefCallback(
    (e) => {
      // Formatting
      const ctrlKey = isMac() ? e.metaKey : e.ctrlKey;

      if (ctrlKey) {
        switch (e.keyCode) {
          case 32: {
            var disallowedTags = [
              "b",
              "strong",
              "i",
              "em",
              "sup",
              "sub",
              "code",
              "mark",
              "a",
              "u",
              "s",
            ];
            var allowedTags = sanitizeConf.allowedTags.filter(function (el) {
              return disallowedTags.indexOf(el) < 0;
            });
            let conf = {
              ...sanitizeConf,
              allowedTags: allowedTags,
              // disallowedTagsMode: "escape",
            };
            var selHTML = getSelectedHtml(editorDocument);

            var formattedHTML = sanitizeHtml(selHTML, conf);

            var selection = editorDocument.getSelection();

            replaceSelectedHtml(selection, editorDocument, formattedHTML);

            var currentEditableRichText =
              selection.anchorNode?.parentElement.closest(
                ".grigora-richtext-input__field[contenteditable='true']",
              );

            emitOnInput(currentEditableRichText);

            e.preventDefault();
            break;
          }
          case 35: {
            let formatType = "highlight";
            if (activeFormats.includes(formatType)) {
              removeFormat(formatType);
            } else {
              applyFormat(formatType);
            }
            e.preventDefault();
            break;
          }
          case 66: {
            let formatType = "bold";
            if (activeFormats.includes(formatType)) {
              removeFormat(formatType);
            } else {
              applyFormat(formatType);
            }
            e.preventDefault();
            break;
          }
          case 73: {
            let formatType = "italic";
            if (activeFormats.includes(formatType)) {
              removeFormat(formatType);
            } else {
              applyFormat(formatType);
            }
            e.preventDefault();
            break;
          }
          case 85: {
            let formatType = "underline";
            if (activeFormats.includes(formatType)) {
              removeFormat(formatType);
            } else {
              applyFormat(formatType);
            }
            e.preventDefault();
            break;
          }
          case 188: {
            if (e.shiftKey) {
              return;
            }
            let formatType = "superscript";
            if (activeFormats.includes(formatType)) {
              removeFormat(formatType);
            } else {
              applyFormat(formatType);
            }
            e.preventDefault();
            break;
          }
          case 190: {
            if (e.shiftKey) {
              return;
            }
            let formatType = "subscript";
            if (activeFormats.includes(formatType)) {
              removeFormat(formatType);
            } else {
              applyFormat(formatType);
            }
            e.preventDefault();
            break;
          }
          case 220: {
            removeAllFormat([
              "highlight",
              "italic",
              "bold",
              "strikethrough",
              "superscript",
              "subscript",
              "underline",
              "code",
            ]);
            e.preventDefault();
            break;
          }
          // remove paragraph indentation
          case 219: {
            var blockIDs = query.getEvent("selected").all();
            blockIDs.forEach((blockID) => {
              var blockProps = query.node(blockID).get().data.props ?? {};
              if (blockProps.layoutMargin === undefined) {
                actions.setProp(blockID, (props) => {
                  props.layoutMargin = {
                    left: "0px",
                    top: "0px",
                    bottom: "0px",
                    right: "0px",
                  };
                });
              } else {
                let currentValue = Number(
                  blockProps.layoutMargin.left.match(/(\d+)/)[0],
                );
                if (currentValue >= 40) {
                  actions.setProp(blockID, (props) => {
                    props.layoutMargin.left = `${currentValue - 40}px`;
                  });
                } else {
                  actions.setProp(blockID, (props) => {
                    props.layoutMargin.left = "0px";
                  });
                }
              }
            });
            e.preventDefault();
            break;
          }
          // add paragraph indentation
          case 221: {
            var blockIDs = query.getEvent("selected").all();
            blockIDs.forEach((blockID) => {
              var blockProps = query.node(blockID).get().data.props ?? {};
              if (blockProps.layoutMargin === undefined) {
                actions.setProp(blockID, (props) => {
                  props.layoutMargin = {
                    left: "40px",
                    top: "0px",
                    bottom: "0px",
                    right: "0px",
                  };
                });
              } else {
                let currentValue = Number(
                  blockProps.layoutMargin.left.match(/(\d+)/)[0],
                );
                actions.setProp(blockID, (props) => {
                  props.layoutMargin.left = `${currentValue + 40}px`;
                });
              }
            });
            e.preventDefault();
            break;
          }
          default:
            break;
        }

        if (e.shiftKey) {
          switch (e.keyCode) {
            case 88: {
              let formatType = "strikethrough";
              if (activeFormats.includes(formatType)) {
                removeFormat(formatType);
              } else {
                applyFormat(formatType);
              }
              e.preventDefault();
              break;
            }
            default:
              break;
          }
        }
      }
    },
    [editorDocument, applyFormat],
  );

  if (isNewPageContentLoading) {
    return <Loader />;
  }

  return (
    <>
      <div
        ref={helperRef}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setLeftWidget("");
          setIsAddingBlocks(false);
          setIsAddingSections(false);
          setIsHomeMenuOpen(false);
          setIsPublishMenuOpen(false);
          hideContextMenu();
          helperRef.current.style.pointerEvents = "none";
        }}
        onDragOver={throttledOnDragOver.current}
        className="helper-container absolute top-0 bottom-0 left-0 right-0 pointer-events-none"
      >
        &nbsp;
      </div>
      <ContextMenu />
      {failedLoadingPageContents[currentPage] && (
        <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center bg-[rgb(239,240,243)]">
          <div className="flex flex-col gap-2 text-xs">
            Failed to load this page.{" "}
            <div className="flex justify-center">
              <button
                onClick={() => {
                  changeCurrentPage(currentPage);
                }}
                className="bg-blue-500 text-white px-4 py-2 rounded-[21px] mx-auto"
              >
                <FontAwesomeIcon
                  icon={faRefresh}
                  className="w-[10px] h-[10px]"
                />{" "}
                Try again
              </button>
            </div>
          </div>
        </div>
      )}
      <FunctionalIFrameComponent
        isComponentPage={currentPage && currentPage.startsWith("component-")}
        // ref={(dom) => connectors.connect(dom)}
        title="functional-iframe"
      >
        <link
          type="text/css"
          rel="stylesheet"
          id="grigora-global-css"
          href={`${process.env.PUBLIC_URL}/css/editor-isolate.css`}
        />

        <style>
          {`
        :root{
          ${
            project.colors &&
            Array.isArray(project.colors) &&
            project.colors
              .map((item) => {
                return `--gb--${item.title}: ${item.color};`;
              })
              .join("")
          }
        }
        .helper-container{
          background-color:red;
        }

        ${typographyCSS}
        ${fontFamilyCSS}

        `}
        </style>
        <div
          className="grigora-loading-overlay"
          style={{
            position: "absolute",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            zIndex: "99",
            backgroundColor: "#ffffff",
          }}
        ></div>
        <main
          onKeyDown={handleOnKeyDown}
          className="grigora-editor-renderer"
          ref={(ref) => connectors.select(connectors.hover(ref, null), null)}
          style={{
            // display: 'flex',
            overflow: "auto",
            width: "100%",
            height: "100%",
          }}
        >
          <div
            ref={canvasRef}
            className="grigora-editor-renderer-canvas flex-1 overflow-x-hidden bg-white drop-shadow-lg w-full h-full"
            style={{
              position: "relative",
              overflowX: "hidden",
              width: "100%",
              height: "100%",
              flex: "1",
              padding: "1px",
            }}
            onCopy={copyHandler}
            onContextMenu={handleContextMenu}
            onMouseDown={(e) => {
              document.querySelector(".helper-container")?.click();
            }}
            // onMouseDown={function (e) {
            // var isRightMB;
            // e = e || window.event;

            // if ("which" in e)  // Gecko (Firefox), WebKit (Safari/Chrome) & Opera
            //   isRightMB = e.which == 3;
            // else if ("button" in e)  // IE, Opera
            //   isRightMB = e.button == 2;

            // console.log(isRightMB);
            // if (isRightMB) {
            //   e.preventDefault();
            //   e.stopPropagation();
            //   return false;
            // }
            // }}
            tabIndex={"0"}
            onKeyDown={(event) => {
              const ctrlKey = isMac() ? event.metaKey : event.ctrlKey;

              // Copy
              if (ctrlKey && event.keyCode === 67) {
                copyHandler(event);
              }

              if (ctrlKey && event.keyCode === 86) {
                // Skip paste event in case of text block
                const node = query.getEvent("selected").first();

                const dom = editorDocument.querySelector(
                  `[data-grigora-block-id="${node}"]`,
                );

                if (
                  dom &&
                  dom.getAttribute("data-grigora-block-type") === "grigora/text"
                ) {
                  return;
                }

                // Paste
                const blocks = getTempStateLocalStorage("copiedBlocks");
                if (blocks) {
                  blockActions.paste(
                    actions,
                    query,
                    null,
                    { grigoraBlockJson: blocks },
                    editorDocument,
                  );
                } else {
                  toast.error(
                    <Toast
                      title={"Error"}
                      description={err?.response?.data?.Output?.message}
                    />,
                  );
                }
              }

              if (ctrlKey && event.keyCode === 88) {
                // Skip the cut event if text is selected
                if (isTextSelected(editorDocument)) {
                  return;
                }
                // Cut
                copyHandler(event);
                blockActions.delete(actions, query);
                toast.success(<Toast title={"Moved To Clipboard"} />, {
                  icon: <CorrectIcon />,
                });
              }
            }}
          >
            <Googlefontloader
              config={{
                google: {
                  families: webloaderFamilies,
                },
              }}
            />
            <CustomFontLoader />
            <Frame></Frame>
          </div>
        </main>
      </FunctionalIFrameComponent>
    </>
  );
}

export default ElementCanvas;
